import { paraFormat } from '../analysis/format';
import { paginate } from '../analysis/pages';
import type { Role } from '../analysis/structure';
import type { DocModel, Paragraph } from '../model/document';
import type { Origin } from '../model/props';
import { truncate } from '../util/text';
import type { FixAction, RuleContext } from './types';

/** Текст в кавычках; уже взятый в «ёлочки» — без вторых кавычек. */
export const quote = (s: string, max = 60): string => {
  const t = truncate(s, max);
  return /^«[^«»]*»$/.test(t) ? t : `«${t}»`;
};

/** Формы слова для счётчика собранных вместе замечаний. */
export const PARAS = ['абзац', 'абзаца', 'абзацев'] as const;

/** Абзацы с данными ролями; по умолчанию без таблиц и надписей. */
export function byRole(ctx: RuleContext, roles: ReadonlySet<Role>, withTables = false): Paragraph[] {
  return ctx.model.paragraphs.filter((p) => !p.textBox && (withTables || !p.cell) && roles.has(ctx.s.roles[p.index]!));
}

export function originNote(model: DocModel, origin: Origin | undefined): string {
  if (!origin) return '';
  if (origin.startsWith('style:')) return ` (задан стилем «${model.styles.displayName(origin.slice(6))}»)`;
  if (origin === 'docDefaults') return ' (так задано по умолчанию в документе)';
  return '';
}

/** Название цвета по-русски и признак «почти чёрного». */
export function colorInfo(hex: string): { name: string; nearBlack: boolean } {
  const m = /^([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/i.exec(hex);
  if (!m) return { name: hex, nearBlack: false };
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h!, 16) / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max < 0.26) return { name: 'почти чёрный', nearBlack: true };
  if (max - min < 0.12) return { name: l > 0.9 ? 'белый' : 'серый', nearBlack: false };
  let h = 0;
  if (max === r) h = ((g - b) / (max - min)) * 60;
  else if (max === g) h = ((b - r) / (max - min)) * 60 + 120;
  else h = ((r - g) / (max - min)) * 60 + 240;
  if (h < 0) h += 360;
  const name =
    h < 15 || h >= 345 ? 'красный'
    : h < 45 ? 'оранжевый'
    : h < 70 ? 'жёлтый'
    : h < 170 ? 'зелёный'
    : h < 200 ? 'бирюзовый'
    : h < 260 ? 'синий'
    : h < 300 ? 'фиолетовый'
    : 'розовый';
  return { name, nearBlack: false };
}

export function normFont(name: string): string {
  return name.trim().toLowerCase().replace(/\s+cyr$/, '');
}

/** Шрифты, которые не считаются нарушением: математика, символы, моноширинные для листингов. */
export const SPECIAL_FONTS =
  /^(cambria math|symbol|wingdings.*|webdings|courier new|courier|consolas|lucida console|menlo|monaco|source code pro|jetbrains mono|fira code|liberation mono|dejavu sans mono|cascadia (code|mono)|pt mono|roboto mono|ubuntu mono|noto (sans )?mono|ms gothic|ms mincho|simsun|simhei|mingliu|pmingliu|batang|gulim|malgun gothic|yu gothic|yu mincho|meiryo)$/i;

/** Гарнитуры, метрически совместимые с Times New Roman. */
export const TNR_EQUIVALENTS = new Set(['liberation serif', 'pt astra serif', 'tinos', 'nimbus roman', 'nimbus roman no9 l', 'times', 'freeserif', 'thorndale']);

/** Удаление точки в конце текста абзаца (многоточие не трогаем). */
export function trailingPeriodFix(p: Paragraph): FixAction | undefined {
  const m = /(?<!\.)\.(\s*)$/.exec(p.text);
  if (!m) return undefined;
  return { kind: 'text', paragraph: p.index, start: m.index, end: m.index + 1, expect: '.', replace: '' };
}

export function hasTrailingPeriod(s: string): boolean {
  return /(?<!\.)\.\s*$/.test(s);
}

/** Первая буква после offset → прописная. */
export function upperFirstFix(p: Paragraph, from = 0): FixAction | undefined {
  const re = /\p{L}/gu;
  re.lastIndex = from;
  const m = re.exec(p.text);
  if (!m || m[0] === m[0].toUpperCase()) return undefined;
  return { kind: 'text', paragraph: p.index, start: m.index, end: m.index + 1, expect: m[0], replace: m[0].toUpperCase() };
}

export const ALIGN_RU: Record<string, string> = {
  left: 'по левому краю',
  right: 'по правому краю',
  center: 'по центру',
  justify: 'по ширине',
  unknown: 'в надписи',
};

const MAIN_SIZE_ROLES = new Set<Role>(['body', 'list', 'formula-where', 'bib']);

/** Абзацы, по которым определяется гарнитура документа. */
export const FONT_ROLES = new Set<Role>(['body', 'list', 'note', 'formula-where', 'bib', 'caption', 'heading', 'appendix-title', 'table-cell']);

/**
 * Основная гарнитура: самая частая из допустимых профилем (main — её имя в нижнем регистре)
 * или первая допустимая, если текст набран другими.
 */
export function mainFont(ctx: RuleContext): { font: string; main?: string } {
  const { model, s, profile } = ctx;
  const allowed = new Set(profile.text.fonts.map(normFont));
  const used = new Map<string, number>();
  for (const p of model.paragraphs) {
    if (p.textBox || p.inToc || !FONT_ROLES.has(s.roles[p.index]!)) continue;
    for (const [font, n] of paraFormat(model, p).fonts) {
      const nf = normFont(font);
      if (allowed.has(nf)) used.set(nf, (used.get(nf) ?? 0) + n);
    }
  }
  const main = [...used].sort((a, b) => b[1] - a[1])[0]?.[0];
  return { font: profile.text.fonts.find((f) => normFont(f) === main) ?? profile.text.fonts[0] ?? 'Times New Roman', main };
}

/** Самый частый кегль основного текста без приложений, полупункты. */
export function mainTextSize(ctx: RuleContext): number | undefined {
  const freq = new Map<number, number>();
  for (const p of byRole(ctx, MAIN_SIZE_ROLES)) {
    if (ctx.s.regions[p.index] === 'appendix') continue;
    for (const [sz, n] of paraFormat(ctx.model, p).sizes) freq.set(sz, (freq.get(sz) ?? 0) + n);
  }
  return [...freq].sort((a, b) => b[1] - a[1])[0]?.[0];
}

/**
 * Число страниц: из свойств файла или по оценке вёрстки. Файлы, собранные скриптами
 * (python-docx, pandoc), часто сохраняют в свойствах «1 страница» из шаблона — тогда верим оценке.
 */
export function estimatePages(model: DocModel): number {
  const estimated = paginate(model);
  const pages = model.meta.pages;
  if (!pages || (pages <= 1 && estimated.total > 2)) return estimated.total;
  return pages;
}
